import { characterOptions, heroFromBuild } from '../../../content/characters/character-options.js';
import { chooseHumanCosmetic, isCosmeticSlot } from '../../../content/characters/rigs/human-markings.js';
import { BODY_SHAPE_FIELDS, bodyShape, bodyShapeWord } from '../../../content/characters/rigs/humanoid-shape.js';
import { FACE_SHAPE_FIELDS, faceShape, faceShapeWord, randomFaceShape, type FaceShapeKey } from '../../../content/characters/rigs/humanoid-face-shape.js';
import { inventorySlots } from '../../../shared/game/supply-types.js';
import {
  MAX_NAME_LENGTH, SKILLS, STAT_ARRAYS, STAT_LABELS, STATS, ancestryBodies, defaultVoice, partVariants, skillTotals, suggestedAttributes, validateCharacterBuild,
  type CharacterBuild, type StatArrayId,
} from '../../../shared/game/character-build.js';
import type { ClassDefinition, Stat } from '../../../shared/game/class-types.js';
import { heroMaxHitDice, type HeroDefinition } from '../../../shared/game/world-types.js';
import { PartyStage } from '../../rendering/entities/party-stage.js';
import type { CharacterStore } from '../../characters/character-store.js';
import { playSlab } from '../../audio/slab-sound.js';
import { previewVoice } from '../../audio/hero-voice.js';
import { icon, type HudIcon } from '../icons.js';
import { setTooltip } from '../tooltips.js';
import { gearIcon } from './gear-icons.js';
import { abilityIcon, classIcon } from '../content-icons.js';
import { slideIn } from '../shell/slab-motion.js';
import { portraitFrame, portraits, portraitsFor } from '../portraits.js';

type Step = 'class' | 'ancestry' | 'sheet' | 'looks';
const STEPS: { id: Step; title: string; book: string }[] = [
  { id: 'class', title: 'Class', book: 'Step 1 · Choose your class' },
  { id: 'ancestry', title: 'Ancestry', book: 'Step 2 · Ancestry & background' },
  { id: 'sheet', title: 'Stats & skills', book: 'Step 3 · Fill in your character sheet' },
  { id: 'looks', title: 'Name & looks', book: 'Basic details' },
];
const SKIN = [0xf1c9a5, 0xe0ac87, 0xd5a384, 0xc99776, 0xa8714f, 0x8d5a3b, 0x6b4129, 0x4a2c1d];
const HAIR = [0x1c1714, 0x382c29, 0x422d25, 0x6b4428, 0x9a6a3c, 0xc9a165, 0xa64b2a, 0x8f8a84];
const HERO = [0xe4b56c, 0x78c6bc, 0xd9776a, 0x8fb4e8, 0xb48fe0, 0x9cc46b, 0xe08fb8, 0xd0d0c8];
const hex = (color: number) => '#' + color.toString(16).padStart(6, '0');
/** The light each class gives its tablet and pedestal. */
const CLASS_ACCENT: Record<string, number> = { commander: 0xe4b56c, hunter: 0x78c6bc, songweaver: 0xb6a6e0, cheat: 0x8fa77a, shepherd: 0xe6d6a0, shadowmancer: 0x8e80a8, berserker: 0xc4553c, mage: 0x7fa6e6, oathsworn: 0xf0cf6a };
const accentOf = (classId: string) => CLASS_ACCENT[classId] ?? HERO[0];
const STAT_ICON: Record<Stat, HudIcon> = { strength: 'strength', dexterity: 'rush', intelligence: 'intellect', will: 'willpower' };
const signed = (value: number) => (value >= 0 ? '+' : '−') + Math.abs(value);
const make = <K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = ''): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag); if (className) node.className = className; if (text) node.textContent = text; return node;
};
/** Where a preview stands; the real seat comes from the server when the character enters a room. */
const PREVIEW_SEAT = { id: 'preview', playerSlot: 'preview', spawn: { x: 0, z: 0 } };

function freshBuild(cls: ClassDefinition): CharacterBuild {
  const body = characterOptions.bodies[0];
  return { id: '', name: '', classId: cls.id, ancestryId: characterOptions.ancestries[0].id, backgroundId: null, statArray: 'standard',
    attributes: suggestedAttributes('standard', cls),
    look: { body: body.id, skin: SKIN[3], hair: HAIR[1], cloth: cls.id === 'hunter' ? 0x28615a : 0x34495e, leather: 0x49392e, color: HERO[0], height: Math.round((body.height[0] + body.height[1]) / 2 * 100) / 100,
      parts: defaultParts(body.id), ...defaultPortrait(body.id, cls.id) }, voice: defaultVoice(characterOptions, body.id) };
}
/** The portraits in the order a body is offered them: its own first (its class's leading), then its ancestry's, then everyone's. */
const bodyPortraits = (body: string, classId: string) => portraitsFor(body, body.replace(/-(male|female)$/, ''), classId);
/** A body's first portrait for a class, as a look field (none while the folder is empty). */
const defaultPortrait = (body: string, classId: string): { portrait?: string } => { const first = bodyPortraits(body, classId)[0]; return first ? { portrait: first.id } : {}; };
/** The head parts the Face section offers, in its order; the other part slots stay under Features or Makeup & tattoos. */
const FACE_SLOTS = ['face', 'mouth', 'eyes', 'eyebrows', 'nose'], MARK_SLOTS = ['scar', 'burn'];
/** Each part slot's default on this body. */
const defaultParts = (body: string) => Object.fromEntries(Object.keys(characterOptions.parts).map(slot => [slot, partVariants(characterOptions, slot, body)[0].id]));

/**
 * The main menu's character creator (Core Rules pp. 18–23). Backgrounds are not offered yet.
 * It only edits a build; the account store saves it and the server re-checks it before any hero is deployed.
 */
export class CharacterCreator {
  private build: CharacterBuild;
  private step: Step = 'class';
  private stage: PartyStage | null = null;
  private previewTimer: ReturnType<typeof setTimeout> | undefined;
  private saving = false;
  private readonly body: HTMLElement;
  private readonly sheet: HTMLElement;
  private readonly steps: HTMLElement;
  private readonly error: HTMLElement;
  private readonly back: HTMLButtonElement;
  private readonly next: HTMLButtonElement;
  private readonly title: HTMLElement;
  private readonly preview: HTMLElement;
  private readonly previewName: HTMLElement;
  private readonly closeUp: HTMLButtonElement;
  /** The looks page's part lists and face sliders, so a preset, Randomize or the body's age can move them in place. */
  private partSelects = new Map<string, HTMLSelectElement>();
  private faceSliders = new Map<FaceShapeKey, { input: HTMLInputElement; output: HTMLOutputElement }>();

  constructor(private host: HTMLElement, private store: CharacterStore, private hooks: { done: (saved: CharacterBuild | null) => void }) {
    this.build = freshBuild(characterOptions.classes[0]);
    host.replaceChildren();
    this.title = make('h1', '', 'Create character'); this.title.id = 'creator-title';
    const header = make('header', 'creator-header');
    const seal = make('span', 'creator-seal'); seal.append(icon('party'));
    header.append(seal, this.title);
    this.steps = make('ol', 'creator-steps');
    const layout = make('div', 'creator-layout');
    const aside = make('div', 'creator-aside');
    this.preview = make('div', 'party-stage creator-stage'); this.preview.style.setProperty('--count', '1');
    const loader = make('div', 'creator-preview-loader');
    this.preview.append(loader);
    this.previewName = make('p', 'creator-preview-name');
    // Head parts are small at full height; the close-up frames the head and shoulders.
    this.closeUp = make('button', 'quiet creator-closeup', 'Close-up'); this.closeUp.type = 'button'; this.closeUp.setAttribute('aria-pressed', 'false');
    this.closeUp.addEventListener('click', () => this.setCloseUp(this.closeUp.getAttribute('aria-pressed') !== 'true'));
    const chamber = make('div', 'creator-chamber'); chamber.append(this.preview, this.closeUp, this.previewName);
    this.sheet = make('dl', 'creator-sheet');
    const record = make('section', 'creator-record');
    record.append(make('h2', 'creator-plaque', 'Character record'), this.sheet);
    aside.append(chamber, record);
    this.body = make('div', 'creator-body');
    layout.append(aside, this.body);
    this.error = make('p', 'error');
    const actions = make('div', 'creator-actions');
    const cancel = make('button', 'secondary creator-cancel', 'Cancel'); cancel.type = 'button'; cancel.addEventListener('click', () => this.hooks.done(null));
    this.back = make('button', 'secondary', 'Back'); this.back.type = 'button'; this.back.addEventListener('click', () => this.go(-1));
    this.next = make('button', 'primary', 'Next'); this.next.type = 'button'; this.next.addEventListener('click', () => this.step === 'looks' ? void this.save() : this.go(1));
    actions.append(cancel, this.back, this.next);
    host.append(header, this.steps, layout, this.error, actions);
    store.onChange(() => this.refreshAccess());
  }

  /** Start a new character, or edit a saved one. */
  open(existing?: CharacterBuild) {
    this.build = existing ? structuredClone(existing) : freshBuild(characterOptions.classes[0]);
    // Slots added since a character was saved (scars and burns, say) start at their default, as the server reads them.
    for (const [slot, id] of Object.entries(defaultParts(this.build.look.body))) if (!characterOptions.parts[slot].defaultBy) this.build.look.parts[slot] ??= id;
    this.step = existing ? 'looks' : 'class'; this.error.textContent = ''; this.saving = false;
    this.title.textContent = existing ? `Edit ${existing.name}` : 'Create character';
    this.refreshAccess(); this.render();
    this.stage ??= new PartyStage(this.preview);
    this.setCloseUp(false);
    this.showPreview(true);
    this.host.querySelector<HTMLElement>('.creator-body input, .creator-body button')?.focus();
  }
  private refreshAccess() {
    this.next.disabled = this.saving;
    this.next.textContent = this.saving ? 'Saving…' : this.step === 'looks' ? 'Save Character' : 'Next';
  }

  private get cls() { return characterOptions.classes.find(cls => cls.id === this.build.classId)!; }
  /** The class's disadvantaged save; an ancestry with Highborn shows it struck through, rolled neutral. */
  private disadvantagedSave(stat: Stat, text: string) {
    const highborn = !!this.ancestry.highborn, chip = make('span', 'class-save ' + (highborn ? 'down neutral' : 'down'), text);
    const tip = highborn ? `${this.ancestry.trait}: ${STAT_LABELS[stat]} saves are neutral instead of disadvantaged` : `Disadvantage on ${STAT_LABELS[stat]} saves`;
    setTooltip(chip, tip, tip); chip.tabIndex = 0; return chip;
  }
  private get ancestry() { return characterOptions.ancestries.find(ancestry => ancestry.id === this.build.ancestryId)!; }

  private go(direction: 1 | -1) {
    const index = STEPS.findIndex(step => step.id === this.step) + direction;
    if (index < 0 || index >= STEPS.length) return;
    if (direction > 0) { const problem = this.problem(this.step); if (problem) { this.error.textContent = problem; return; } }
    this.step = STEPS[index].id; this.error.textContent = ''; this.render();
    // Each step arrives as a slab: forward from the right, back from the left, with the stone scrape.
    slideIn(this.body, direction > 0 ? 'right' : 'left');
    this.body.querySelector<HTMLElement>('input, button, select')?.focus();
  }
  /** What still blocks leaving a step. */
  private problem(step: Step): string | null {
    if (step === 'looks' && !this.build.name.trim()) return 'Give your character a name.';
    return null;
  }

  private async save() {
    const problem = this.problem('sheet') ?? this.problem('looks');
    if (problem) { this.error.textContent = problem; return; }
    this.saving = true; this.refreshAccess(); this.next.textContent = 'Saving…'; this.error.textContent = '';
    try {
      // The store assigns an id on first save; validate with a stand-in so rule errors show before any network call.
      validateCharacterBuild({ ...this.build, id: this.build.id || 'new' }, characterOptions);
      const saved = await this.store.save(this.build);
      this.hooks.done(saved);
    } catch (error) {
      this.error.textContent = (error as { code?: string }).code === 'permission-denied' ? 'Your account could not save this character. Sign in again and retry.' : (error as Error).message;
    } finally { this.saving = false; this.refreshAccess(); }
  }

  private render() {
    const currentIndex = STEPS.findIndex(step => step.id === this.step);
    this.steps.replaceChildren(...STEPS.map((step, index) => {
      const item = make('li', step.id === this.step ? 'current' : index < currentIndex ? 'complete' : '');
      item.append(make('span', 'creator-step-number', String(index + 1)), make('span', 'creator-step-label', step.title));
      if (step.id === this.step) item.setAttribute('aria-current', 'step'); return item;
    }));
    const current = STEPS.find(step => step.id === this.step)!;
    this.body.replaceChildren(make('h2', 'creator-heading', current.book));
    ({ class: () => this.renderClass(), ancestry: () => this.renderAncestry(), sheet: () => this.renderSheet(), looks: () => this.renderLooks() })[this.step]();
    this.back.hidden = this.step === 'class';
    this.refreshAccess(); this.renderSummary();
  }

  /**
   * Classes are two carved tablets; the chosen one sinks and lights its gem, the stage flares, and only its sheet is shown below.
   * Choosing rebuilds the sheet in place, not the whole step, so the tablets can animate.
   */
  private renderClass() {
    const picker = make('div', 'class-picker');
    const tablets = make('div', 'class-tablets'); tablets.setAttribute('role', 'radiogroup'); tablets.setAttribute('aria-label', 'Class');
    const detail = make('div', 'class-detail');
    const show = () => {
      detail.style.setProperty('--accent', hex(accentOf(this.cls.id)));
      detail.replaceChildren(this.classSheet(this.cls));
      detail.classList.remove('arriving'); void detail.offsetWidth; detail.classList.add('arriving');
    };
    const labels = new Map<string, HTMLElement>();
    for (const cls of characterOptions.classes) {
      const tablet = make('label', 'tablet class-tablet' + (cls.id === this.build.classId ? ' chosen' : '')); tablet.style.setProperty('--accent', hex(accentOf(cls.id)));
      const input = make('input'); input.type = 'radio'; input.name = 'creator-class'; input.value = cls.id; input.checked = cls.id === this.build.classId;
      input.addEventListener('change', () => {
        // A class change keeps the chosen array but moves its best values to the new Key stats.
        const previous = accentOf(this.build.classId), previousClass = this.build.classId;
        this.build.classId = cls.id; this.build.attributes = suggestedAttributes(this.build.statArray, cls);
        if (this.build.look.color === previous) this.build.look.color = accentOf(cls.id);
        const { look } = this.build;
        if (look.portrait === defaultPortrait(look.body, previousClass).portrait) { delete look.portrait; Object.assign(look, defaultPortrait(look.body, cls.id)); }
        for (const [id, label] of labels) label.classList.toggle('chosen', id === cls.id);
        show(); this.renderSummary(); this.showPreview(false, true); playSlab();
      });
      const crest = icon(classIcon(cls.id)); crest.classList.add('party-crest');
      const emblem = make('span', 'tablet-emblem'); emblem.append(crest);
      const gem = make('span', 'tablet-gem'); gem.setAttribute('aria-hidden', 'true');
      tablet.append(input, gem, emblem, make('strong', 'tablet-name', cls.name), make('span', 'tablet-role', `Key ${cls.keyStats.map(stat => STAT_LABELS[stat]).join(' · ')}`));
      labels.set(cls.id, tablet); tablets.append(tablet);
    }
    show();
    picker.append(tablets, detail, make('p', 'hint class-more', 'More classes will join the roster later.'));
    this.body.append(picker);
  }

  /** What the chosen class starts with, drawn as dice, stat runes, gear slots and power seals rather than a list. */
  private classSheet(cls: ClassDefinition): HTMLElement {
    const sheet = make('div', 'class-sheet');
    const vital = (glyph: HudIcon, value: string, label: string) => {
      const node = make('div', 'class-vital'); node.append(icon(glyph), make('strong', '', value), make('small', '', label)); return node;
    };
    const vitals = make('div', 'class-vitals');
    vitals.append(vital('hitDie', `d${cls.hitDie}`, 'Hit Die'), vital('health', String(cls.startingHp), 'Hit points'));
    const stats = make('div', 'class-stats');
    for (const stat of STATS) {
      const key = cls.keyStats.includes(stat), tile = make('span', 'class-stat' + (key ? ' key' : ''));
      tile.append(icon(STAT_ICON[stat]), make('strong', '', STAT_LABELS[stat]));
      tile.tabIndex = 0; setTooltip(tile, key ? `${STAT_LABELS[stat]} is a key stat for ${cls.name}: the best values go here.` : `${STAT_LABELS[stat]} is not a key stat for ${cls.name}.`, `${STAT_LABELS[stat]}${key ? ', key stat' : ''}`);
      stats.append(tile);
    }
    const saves = make('div', 'class-saves');
    for (const stat of cls.saves.advantage) { const chip = make('span', 'class-save up', STAT_LABELS[stat]); setTooltip(chip, `Advantage on ${STAT_LABELS[stat]} saves`, `Advantage on ${STAT_LABELS[stat]} saves`); chip.tabIndex = 0; saves.append(chip); }
    for (const stat of cls.saves.disadvantage) { const chip = this.disadvantagedSave(stat, STAT_LABELS[stat]); saves.append(chip); }
    const top = make('div', 'class-top'); top.append(vitals, stats, saves);
    const gear = make('ul', 'class-gear');
    for (const name of cls.startingGear) {
      const slot = make('li', 'class-gear-slot'); slot.tabIndex = 0; slot.append(icon(gearIcon(name)), make('small', '', name)); setTooltip(slot, name); gear.append(slot);
    }
    const powers = make('ul', 'class-powers');
    for (const feature of [...cls.abilities, ...cls.passives].filter(entry => entry.level <= 1)) {
      const item = make('li', 'class-power'), seal = make('span', 'class-power-seal'); seal.append(icon(abilityIcon(feature.id)));
      const text = make('div'); text.append(make('strong', '', feature.name), make('p', '', feature.description)); item.append(seal, text); powers.append(item);
    }
    sheet.append(make('p', 'class-flavor', cls.summary), top, make('h3', '', 'Starting gear'), gear, make('h3', '', 'Powers'), powers);
    return sheet;
  }

  private renderAncestry() {
    for (const ancestry of characterOptions.ancestries) {
      const card = make('button', 'creator-choice' + (ancestry.id === this.build.ancestryId ? ' chosen' : '')); card.type = 'button';
      card.setAttribute('aria-pressed', String(ancestry.id === this.build.ancestryId));
      card.append(make('strong', 'creator-choice-name', `${ancestry.name} (${ancestry.size === 'medium' ? 'Medium' : 'Small'})`), make('span', 'hint', ancestry.description));
      const trait = make('p'); trait.append(make('strong', '', ancestry.trait + '. '), ancestry.traitDescription ?? `+${ancestry.skillBonus} to all skills and Initiative.`); card.append(trait);
      if (ancestry.languageDescription) card.append(make('p', 'hint', ancestry.languageDescription));
      card.addEventListener('click', () => {
        if (ancestry.id === this.build.ancestryId) return;
        const female = this.build.look.body.endsWith('-female'), bodies = ancestryBodies(characterOptions, ancestry.id);
        const body = bodies.find(body => body.id.endsWith(female ? '-female' : '-male')) ?? bodies[0];
        this.build.ancestryId = ancestry.id; this.changeBody(body);
        this.build.look.skin = ancestry.skinColors?.[0] ?? SKIN[3];
        this.render(); this.showPreview();
      });
      this.body.append(card);
    }
    const background = make('div', 'creator-choice locked');
    background.append(make('strong', 'creator-choice-name', 'Background'), make('span', 'hint', 'Backgrounds are not available yet. Your hero starts with class equipment only.'));
    this.body.append(background);
  }

  private renderSheet() {
    const { build } = this, cls = this.cls;
    // Stat array choice.
    const arrays = make('div', 'creator-segmented'); arrays.setAttribute('role', 'radiogroup'); arrays.setAttribute('aria-label', 'Stat array');
    for (const [id, array] of Object.entries(STAT_ARRAYS) as [StatArrayId, typeof STAT_ARRAYS[StatArrayId]][]) {
      const label = make('label', id === build.statArray ? 'chosen' : '');
      const input = make('input'); input.type = 'radio'; input.name = 'creator-array'; input.checked = id === build.statArray;
      input.addEventListener('change', () => { build.statArray = id; build.attributes = suggestedAttributes(id, cls); this.render(); this.showPreview(); });
      label.append(input, make('strong', '', array.name), make('span', '', array.values.map(signed).join(' ')));
      arrays.append(label);
    }
    const stats = make('div', 'creator-stats');
    for (const stat of STATS) {
      const row = make('div', 'creator-stat' + (cls.keyStats.includes(stat) ? ' key' : '')); row.setAttribute('role', 'group'); row.setAttribute('aria-label', `${STAT_LABELS[stat]} value`);
      // The value is a counter: the arrows move it to the next value in the array, and the stat that held that value takes the old one.
      const values = [...new Set(STAT_ARRAYS[build.statArray].values)].sort((x, y) => y - x), index = values.indexOf(build.attributes[stat]);
      const step = (label: 'Raise' | 'Lower', target: number | undefined) => {
        const button = make('button', 'stat-step ' + (label === 'Raise' ? 'up' : 'down')); button.type = 'button'; button.disabled = target === undefined;
        const name = `${label} ${STAT_LABELS[stat]}`; button.setAttribute('aria-label', name);
        button.addEventListener('click', () => { if (target === undefined) return; this.assign(stat, target); this.body.querySelector<HTMLElement>(`[aria-label="${name}"]:not(:disabled)`)?.focus(); });
        return button;
      };
      const counter = make('div', 'stat-counter');
      counter.append(step('Lower', index >= 0 ? values[index + 1] : undefined), make('output', 'stat-value', signed(build.attributes[stat])), step('Raise', index > 0 ? values[index - 1] : undefined));
      // Every tile has the same three rows (name, tags, value) so the four line up whatever tags a stat carries.
      const name = make('span', 'creator-stat-name'); name.append(icon(STAT_ICON[stat]), make('strong', '', STAT_LABELS[stat]));
      const tags = make('span', 'creator-stat-tags');
      if (cls.keyStats.includes(stat)) { const chip = make('span', 'class-save key', 'Key'); setTooltip(chip, `A key stat for ${cls.name}: the best values go here.`); chip.tabIndex = 0; tags.append(chip); }
      if (cls.saves.advantage.includes(stat)) { const chip = make('span', 'class-save up', 'Save'); setTooltip(chip, `Advantage on ${STAT_LABELS[stat]} saves`); chip.tabIndex = 0; tags.append(chip); }
      else if (cls.saves.disadvantage.includes(stat)) tags.append(this.disadvantagedSave(stat, 'Save'));
      row.append(name, tags, counter);
      stats.append(row);
    }
    const reset = make('button', 'quiet', 'Use the book’s suggestion'); reset.type = 'button';
    reset.addEventListener('click', () => { build.attributes = suggestedAttributes(build.statArray, cls); this.render(); this.showPreview(); });
    // Skills.
    const totals = skillTotals(build, this.ancestry);
    const skills = make('div', 'creator-skills');
    for (const skill of SKILLS) {
      const row = make('div', 'creator-skill');
      row.append(make('span', 'creator-skill-name', skill.name), make('span', 'hint', STAT_LABELS[skill.stat]), make('output', 'creator-skill-total', signed(totals[skill.id])));
      skills.append(row);
    }
    this.body.append(make('h3', '', 'Stats'), make('p', 'hint', 'Pick an array, then choose where each value goes. The best values start in your Key stats; swap any two by choosing a new value.'), arrays, stats, reset,
      make('h3', '', 'Skills'), make('p', 'hint', `Each skill starts at its stat. ${this.ancestry.skillBonus ? `Your ancestry adds +${this.ancestry.skillBonus} to all skills.` : Object.entries(this.ancestry.skillBonuses ?? {}).map(([id, bonus]) => `Your ancestry adds +${bonus} to ${SKILLS.find(skill => skill.id === id)?.name ?? id}.`).join(' ')}`), skills);
  }
  /** Give `stat` this value and hand its old value to the stat that had it, so the array stays whole. */
  private assign(stat: Stat, value: number) {
    const attributes = this.build.attributes, previous = attributes[stat];
    const other = STATS.find(candidate => candidate !== stat && attributes[candidate] === value);
    attributes[stat] = value; if (other) attributes[other] = previous;
    this.render(); this.showPreview();
  }

  /**
   * The looks page: one section per kind of choice, in this order. Each section is built by its own method, so another kind of control
   * is one more method and one more entry here.
   */
  private renderLooks() {
    this.partSelects = new Map(); this.faceSliders = new Map();
    const [features, markings] = this.partSections();
    this.body.append(this.identitySection(), this.bodySection(), this.faceSection(), this.voiceSection(), features, markings, this.colorsSection());
  }
  private section(id: string, title: string | null, ...content: Node[]) {
    const section = make('section', `creator-section creator-section-${id}`);
    if (title) section.append(make('h3', '', title));
    section.append(...content); return section;
  }
  private identitySection() {
    const name = make('input'); name.id = 'creator-name'; name.maxLength = MAX_NAME_LENGTH; name.required = true; name.autocomplete = 'off'; name.value = this.build.name; name.placeholder = 'Your hero’s name';
    name.addEventListener('input', () => { this.build.name = name.value; this.renderSummary(); });
    const nameLabel = make('label', '', 'Name'); nameLabel.append(name);
    return this.section('identity', null, nameLabel, ...portraits.length ? [make('h3', '', 'Portrait'), this.portraitChoice()] : []);
  }
  /** The body, its height and the shape sliders (`BodyShape`), each with a word for where it stands. */
  private bodySection() {
    const { look } = this.build;
    const bodies = make('div', 'creator-segmented'); bodies.setAttribute('role', 'radiogroup'); bodies.setAttribute('aria-label', 'Body');
    for (const body of ancestryBodies(characterOptions, this.build.ancestryId)) {
      const label = make('label', body.id === look.body ? 'chosen' : '');
      const input = make('input'); input.type = 'radio'; input.name = 'creator-body'; input.checked = body.id === look.body;
      input.addEventListener('change', () => {
        this.changeBody(body);
        this.render(); this.showPreview();
      });
      label.append(input, make('strong', '', body.name)); bodies.append(label);
    }
    const body = characterOptions.bodies.find(candidate => candidate.id === look.body)!;
    const sliders = make('div', 'creator-sliders');
    sliders.append(this.slider('Height', body.height, .01, look.height, value => `${value.toFixed(2)} m`, value => { look.height = value; }));
    for (const field of BODY_SHAPE_FIELDS) {
      // Age also lines and slackens the face, while its own Wrinkles and Sag are left to follow it.
      sliders.append(this.slider(field.label, [field.min, field.max], .05, bodyShape(look.shape)[field.key], value => bodyShapeWord(field.key, value),
        value => { look.shape = { ...bodyShape(look.shape), [field.key]: value }; if (field.key === 'age') this.syncFace(); }));
    }
    return this.section('body', 'Body', bodies, sliders);
  }
  private slider(name: string, [min, max]: [number, number], step: number, current: number, text: (value: number) => string, set: (value: number) => void) {
    const input = make('input'); input.type = 'range'; input.min = String(min); input.max = String(max); input.step = String(step); input.value = String(current);
    const output = make('output', '', text(current));
    input.addEventListener('input', () => { const value = Number(input.value); set(value); output.textContent = text(value); this.showPreview(); });
    const label = make('label', 'creator-slider', name); label.append(input, output);
    return label;
  }
  private voiceSection() { return this.section('voice', 'Voice', this.voiceChoice()); }
  /**
   * The face: a preset to start from (choosing one recentres the sliders), the features' own shapes, a slider for each measure of the face
   * (`FaceShape`), the aging layer, and old scars and burns. The die in the heading rolls a new face and leaves age and wounds alone.
   */
  private faceSection() {
    const { look } = this.build;
    const presets = make('div', 'creator-parts'), marks = make('div', 'creator-parts');
    for (const slot of FACE_SLOTS) { const row = this.partRow(slot, slot === 'face' ? 'Preset' : undefined, () => { if (slot === 'face') this.recentreFace(); }); if (row) presets.append(row); }
    for (const slot of MARK_SLOTS) { const row = this.partRow(slot); if (row) marks.append(row); }
    const shape = make('div', 'creator-sliders'), aging = make('div', 'creator-sliders');
    for (const field of FACE_SHAPE_FIELDS) {
      const slider = this.slider(field.label, [field.min, field.max], .05, faceShape(look.faceShape, look.shape)[field.key], value => faceShapeWord(field.key, value),
        value => { look.faceShape = { ...look.faceShape, [field.key]: value }; this.setCloseUp(true); });
      this.faceSliders.set(field.key, { input: slider.querySelector('input')!, output: slider.querySelector('output')! });
      (field.aging ? aging : shape).append(slider);
    }
    const roll = make('button', 'quiet creator-roll'); roll.type = 'button'; roll.append(icon('hitDie')); roll.setAttribute('aria-label', 'Randomize face'); setTooltip(roll, 'Randomize face');
    roll.addEventListener('click', () => this.randomizeFace());
    const heading = make('div', 'creator-section-heading'); heading.append(make('h3', '', 'Face'), roll);
    return this.section('face', null, heading, presets, shape, make('h4', '', 'Age'), aging, make('h4', '', 'Scars & burns'), marks);
  }
  /** A new face: a preset and feature shapes from what this body offers, and fresh sliders; age, scars and burns stay as they were. */
  private randomizeFace() {
    const { look } = this.build, pick = <T,>(items: T[]) => items[Math.floor(Math.random() * items.length)];
    for (const slot of FACE_SLOTS) {
      const variants = partVariants(characterOptions, slot, look.body).filter(variant => slot !== 'eyebrows' || variant.id !== 'none');
      if (variants.length) look.parts[slot] = pick(variants).id;
    }
    look.faceShape = { ...randomFaceShape(), ...this.agingOnly() };
    this.syncFace(); this.setCloseUp(true); this.showPreview(); playSlab();
  }
  /** The look's own Wrinkles and Sag, if it has set them. */
  private agingOnly() {
    return Object.fromEntries(Object.entries(this.build.look.faceShape ?? {}).filter(([key]) => FACE_SHAPE_FIELDS.find(field => field.key === key)?.aging));
  }
  /** A new preset is a new starting point: the shape sliders go back to the middle, aging stays. */
  private recentreFace() { this.build.look.faceShape = this.agingOnly(); this.syncFace(); }
  /** Moves the face's lists and sliders to the look, as after a roll, a preset or a change of the body's age. */
  private syncFace() {
    const { look } = this.build, current = faceShape(look.faceShape, look.shape);
    for (const [key, { input, output }] of this.faceSliders) { input.value = String(current[key]); output.textContent = faceShapeWord(key, current[key]); }
    for (const [slot, select] of this.partSelects) select.value = look.parts[slot];
  }
  /** One part slot as a list with previous and next arrows over the variants this body offers; choosing one frames the head. */
  private partRow(slot: string, title?: string, chosen?: () => void): HTMLElement | null {
    const { look } = this.build, name = title ?? characterOptions.parts[slot].name;
    const variants = partVariants(characterOptions, slot, look.body);
    if (variants.length < 2) return null;
    const select = make('select'); select.id = `creator-part-${slot}`;
    this.partSelects.set(slot, select);
    for (const variant of variants) { const option = make('option', '', variant.name); option.value = variant.id; option.selected = look.parts[slot] === variant.id; select.append(option); }
    const choose = (id: string) => {
      chooseHumanCosmetic(look.parts, slot, id);
      for (const [part, control] of this.partSelects) control.value = look.parts[part];
      chosen?.();
      this.setCloseUp(slot !== 'cape' && slot !== 'bearing' && (!slot.startsWith('tattoo') || ['face', 'legacy'].includes(look.parts.tattooLayout)));
      this.showPreview();
    };
    select.addEventListener('change', () => choose(select.value));
    const step = (label: string, direction: 1 | -1) => {
      const button = make('button', 'secondary creator-step', direction < 0 ? '‹' : '›'); button.type = 'button';
      button.setAttribute('aria-label', `${label} ${name.toLowerCase()}`);
      button.addEventListener('click', () => choose(variants[(variants.findIndex(variant => variant.id === look.parts[slot]) + direction + variants.length) % variants.length].id));
      return button;
    };
    const label = make('label', 'creator-part', name); label.htmlFor = select.id;
    const control = make('div', 'creator-part-control'); control.append(step('Previous', -1), select, step('Next', 1));
    const row = make('div', 'creator-part-row'); row.append(label, control);
    return row;
  }
  /** Hair, facial hair, headwear, cloak and bearing, and makeup and tattoos. */
  private partSections(): [HTMLElement, HTMLElement] {
    const parts = make('div', 'creator-parts'), cosmetics = make('div', 'creator-parts');
    for (const slot of Object.keys(characterOptions.parts)) {
      if (FACE_SLOTS.includes(slot) || MARK_SLOTS.includes(slot)) continue;
      const row = this.partRow(slot);
      if (row) (isCosmeticSlot(slot) ? cosmetics : parts).append(row);
    }
    return [this.section('features', 'Features', parts),
      this.section('markings', 'Makeup & tattoos', make('p', 'hint', 'Mix makeup styles with colours. Choose a tattoo design, placement, size and ink: from face sigils to large sleeves, chest pieces and full body suits. Clothing covers ink beneath it.'), cosmetics)];
  }
  private colorsSection() {
    const { look } = this.build;
    return this.section('colors', 'Colors',
      this.swatches('Skin', this.ancestry.skinColors ?? SKIN, look.skin, color => { look.skin = color; }),
      this.swatches('Hair', this.ancestry.hairColors ?? HAIR, look.hair, color => { look.hair = color; }),
      this.picker('Clothing', look.cloth, color => { look.cloth = color; }),
      this.picker('Leather', look.leather, color => { look.leather = color; }),
      this.swatches('Hero color', HERO, look.color, color => { look.color = color; }));
  }
  /** Every portrait in the folder as a gallery of framed thumbnails, the body's own first. */
  private portraitChoice() {
    const gallery = make('div', 'portrait-gallery'); gallery.setAttribute('role', 'radiogroup'); gallery.setAttribute('aria-label', 'Portrait');
    for (const portrait of bodyPortraits(this.build.look.body, this.build.classId)) {
      const button = make('button', 'portrait-choice'); button.type = 'button'; button.setAttribute('role', 'radio');
      const name = portrait.id.replace(/^[a-z]+-(male|female)-/, '').replace(/-/g, ' ');
      button.setAttribute('aria-checked', String(portrait.id === this.build.look.portrait)); button.setAttribute('aria-label', name); setTooltip(button, name);
      button.append(portraitFrame(portrait.id, classIcon(this.build.classId)));
      button.addEventListener('click', () => {
        this.build.look.portrait = portrait.id;
        for (const other of gallery.querySelectorAll('button')) other.setAttribute('aria-checked', String(other === button));
      });
      gallery.append(button);
    }
    return gallery;
  }
  /** The voice packs as a list with a button that plays a line from the chosen one. */
  private voiceChoice() {
    const select = make('select'); select.id = 'creator-voice'; select.setAttribute('aria-label', 'Voice');
    for (const voice of characterOptions.voices) { const option = make('option', '', voice.name); option.value = voice.id; option.selected = voice.id === this.build.voice; select.append(option); }
    const listen = make('button', 'secondary', 'Listen'); listen.type = 'button';
    const hear = () => previewVoice(this.build.voice, this.build.ancestryId);
    select.addEventListener('change', () => { this.build.voice = select.value; hear(); });
    listen.addEventListener('click', hear);
    const row = make('div', 'creator-part-control'); row.append(select, listen);
    return row;
  }
  private swatches(label: string, colors: number[], current: number, set: (color: number) => void) {
    const group = make('div', 'creator-swatches'); group.setAttribute('role', 'radiogroup'); group.setAttribute('aria-label', label);
    group.append(make('span', 'creator-swatch-label', label));
    for (const color of colors) {
      const button = make('button', 'creator-swatch'); button.type = 'button'; button.style.setProperty('--swatch', hex(color));
      button.setAttribute('role', 'radio'); button.setAttribute('aria-checked', String(color === current)); button.setAttribute('aria-label', `${label} ${hex(color)}`);
      button.addEventListener('click', () => {
        set(color); for (const other of group.querySelectorAll('button')) other.setAttribute('aria-checked', String(other === button));
        this.renderSummary(); this.showPreview();
      });
      group.append(button);
    }
    return group;
  }
  private picker(label: string, current: number, set: (color: number) => void) {
    const wrapper = make('label', 'creator-swatches'); wrapper.append(make('span', 'creator-swatch-label', label));
    const input = make('input'); input.type = 'color'; input.value = hex(current);
    input.addEventListener('input', () => { set(parseInt(input.value.slice(1), 16)); this.showPreview(); });
    wrapper.append(input); return wrapper;
  }

  private hero(): HeroDefinition | null {
    try { return heroFromBuild({ ...this.build, id: this.build.id || 'preview', name: this.build.name.trim() || 'Unnamed' }, PREVIEW_SEAT); }
    catch { return null; }
  }
  private renderSummary() {
    const hero = this.hero(), cls = this.cls;
    this.previewName.textContent = this.build.name.trim() || cls.name;
    this.sheet.replaceChildren();
    if (!hero) return;
    const { profile } = hero;
    const rows: [string, string][] = [
      ['Name', this.build.name.trim() || '—'], ['Class', `${cls.name} · Level 1`], ['Ancestry', this.ancestry.name],
      ['Ancestry trait', `${this.ancestry.trait}: ${this.ancestry.traitDescription ?? ''}`],
      ['Ancestry languages', hero.languages?.map(language => language.id === 'goblin' ? `${language.name} (Goblin)` : language.name).join(', ') || '—'],
      ['Stats', STATS.map(stat => `${STAT_LABELS[stat]} ${signed(this.build.attributes[stat])}`).join('  ')],
      ['Hit points', `${profile.maxHp} · Hit Die 1d${cls.hitDie}`], ['Defense', String(profile.defense)], ['Initiative', `${signed(profile.initiative ?? 0)}${profile.initiativeAdvantage ? ' · Advantage' : ''}`],
      ['Hit Dice', `${heroMaxHitDice(hero)} · d${cls.hitDie}`], ['Speed', String(profile.movementDistance / 1.5)], ['Wounds', String(profile.maxWounds)], ['Inventory', `${inventorySlots(profile.attributes.strength)} slots`],
      ['Weapons', profile.attacks.map(attack => attack.name).join(', ')], ['Armor', cls.startingGear.find(item => /hides|mail|armor|garb/i.test(item)) ?? '—'],
    ];
    for (const [term, detail] of rows) this.sheet.append(make('dt', '', term), make('dd', '', detail));
  }
  private setCloseUp(on: boolean) {
    this.closeUp.setAttribute('aria-pressed', String(on)); this.closeUp.textContent = on ? 'Full figure' : 'Close-up';
    this.stage?.setCloseUp(on);
  }
  private changeBody(body: (typeof characterOptions.bodies)[number]) {
    const { look } = this.build;
    const previousDefaults = defaultParts(look.body);
    if (this.build.voice === defaultVoice(characterOptions, look.body)) this.build.voice = defaultVoice(characterOptions, body.id);
    // Like the voice, the portrait follows the body until one is picked.
    if (look.portrait === defaultPortrait(look.body, this.build.classId).portrait) { delete look.portrait; Object.assign(look, defaultPortrait(body.id, this.build.classId)); }
    look.body = body.id; look.height = Math.min(body.height[1], Math.max(body.height[0], look.height));
    for (const [slot, fallback] of Object.entries(defaultParts(body.id))) if (look.parts[slot] === previousDefaults[slot] || !partVariants(characterOptions, slot, body.id).some(variant => variant.id === look.parts[slot])) look.parts[slot] = fallback;
  }
  private showPreview(now = false, flare = false) {
    clearTimeout(this.previewTimer);
    // Rebuilding the model on every slider tick is wasteful; settle first.
    this.previewTimer = setTimeout(() => { const hero = this.hero(); if (hero && this.stage) this.stage.show([hero], new Set([hero.id]), flare); }, now ? 0 : 180);
  }
}
